/* eslint-disable jsx-a11y/label-has-associated-control */
import * as React from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Stack,
  Avatar,
  Button,
  IconButton,
  useTheme,
  useMediaQuery,
  Alert,
  Slide,
  Grid,
} from '@mui/material';
import Iconify from 'components/Iconify'; // your existing import path
import { LoadingButton } from '@mui/lab';
import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, RHFTextField } from 'components/hook-form';
import { useSnackbar } from 'notistack';
import axiosInstance, { getErrorMessage } from 'utils/axios';
import CRUD from 'utils/Crud';

const Transition = React.forwardRef((props, ref) => (
  <Slide direction="left" ref={ref} {...props} />
));

const validationSchema = Yup.object({
  first_name: Yup.string().required('First name is required'),
  last_name: Yup.string().required('Last name is required'),
  email: Yup.string().email('Enter a valid email').required('Email is required'),
  display_name: Yup.string().required('Display name is required'),
  phone: Yup.string().required("Phone number is required in international format"),
  referral_id: Yup.string().required("Promo code is required"),
  confirmPassword: Yup.string().required('Please confirm with your password'),
  imageFile: Yup.mixed().nullable(),
});

EditUserProfileInfoModal.propTypes = {
  // open: PropTypes.bool,
  handleClose: PropTypes.func,
  handleComplete: PropTypes.func,
  user: PropTypes.object.isRequired,
};

export default function EditUserProfileInfoModal({ user, handleClose, handleComplete }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { enqueueSnackbar } = useSnackbar();

  const methods = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      email: user.email || '',
      phone: user.phone || '',
      referral_id: user.referral_id || '',
      display_name: user.display_name || '',
      imageFile: null,
      confirmPassword: '',
    },
  });

  const {
    watch,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors, isSubmitting },
    reset,
  } = methods;

  const [previewMode, setPreviewMode] = React.useState(false);
  const [serverError, setServerError] = React.useState('');
  const [imagePreview, setImagePreview] = React.useState(null);

  const watchImage = watch('imageFile');

  React.useEffect(() => {
    if (!watchImage || watchImage.length === 0) {
      setImagePreview(null);
      return;
    }
    const file = watchImage[0];
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);
  }, [watchImage]);

  const formValues = getValues();
  const changedFields = {};
  ['first_name', 'last_name', 'display_name', 'email', 'phone', 'referral_id'].forEach((field) => {
    if ((user[field] ?? '') !== (formValues[field] ?? '')) changedFields[field] = true;
  });
  if (imagePreview) changedFields.image = true;

  const handlePreview = () => {
    setServerError('');
    methods.handleSubmit(() => setPreviewMode(true))();
  };

  const onSubmit = async (data) => {
    setServerError('');
    try {
      const formData = new FormData();
      formData.append('first_name', data.first_name);
      formData.append('last_name', data.last_name);
      formData.append('email', data.email);
      formData.append('display_name', data.display_name);
      formData.append('phone', data.phone);
      formData.append('referral_id', data.referral_id);
      formData.append('password', data.confirmPassword);
      if (data.imageFile && data.imageFile.length > 0) {
        formData.append('userfile', data.imageFile[0]);
      }
      formData.append('user_id', user.user_id);

      const ret  = await axiosInstance.post(CRUD.user.updateUserProfile, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      enqueueSnackbar('User profile updated successfully', { variant: 'success' });
      reset();
      handleComplete(ret.data)
    } catch (err) {
      const message = getErrorMessage(err);
      setServerError(message);
      enqueueSnackbar(message, { variant: 'error' });
    }
  };

  function PreviewField({ label, field, isImage = false }) {
    const oldValue = user[field];
    const newValue = isImage ? imagePreview : formValues[field];
    const changed = changedFields[field];

    if (isImage) {
      return (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            {label}
          </Typography>
          <Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap">
            <Box>
              <Typography variant="caption" color="textSecondary">
                Current
              </Typography>
              <Avatar
                src={user.image_url || ''}
                alt="Current image"
                sx={{ width: 64, height: 64, borderRadius: 1, border: '1px solid #ccc' }}
              />
            </Box>
            <Box>
              <Typography variant="caption" color="textSecondary">
                New
              </Typography>
              {changed && newValue ? (
                <Avatar
                  src={newValue}
                  alt="New image"
                  sx={{ width: 64, height: 64, borderRadius: 1, border: '2px solid #d32f2f' }}
                />
              ) : (
                <Typography color="text.secondary" fontStyle="italic" mt={2}>
                  No change
                </Typography>
              )}
            </Box>
          </Stack>
        </Box>
      );
    }

    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          {label}
        </Typography>
        <Stack direction="row" spacing={2}>
          <Box sx={{ flex: 1 }}>
            {changed ? (
              <Typography sx={{ color: 'error.main', textDecoration: 'line-through' }}>
                {oldValue || '(empty)'}
              </Typography>
            ) : (
              <Typography>{oldValue || '(empty)'}</Typography>
            )}
          </Box>
          {changed && (
            <Box
              sx={{
                flex: 1,
                bgcolor: (theme) => theme.palette.success.light,
                color: (theme) => theme.palette.success.dark,
                borderRadius: 1,
                px: 1,
                py: 0.5,
                fontWeight: 'bold',
                whiteSpace: 'pre-wrap',
              }}
            >
              {newValue || '(empty)'}
            </Box>
          )}
        </Stack>
      </Box>
    );
  }

  return (
    <Dialog
      open
      onClose={(event, reason) => {
        // Prevent close on backdrop click or escape key press
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          return;
        }
        handleClose();
      }}
      maxWidth="sm"
      fullWidth
      onBackdropClick={()=> false}
      PaperProps={{ sx: { maxHeight: '80vh', position: 'relative' } }}
      aria-labelledby="edit-user-profile-dialog-title"
      TransitionComponent={Transition}
      keepMounted
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {previewMode ? ' Preview Changes' : 'Edit User Profile'}
        <IconButton onClick={handleClose} aria-label="Close dialog" size="large">
          <Iconify icon="eva:close-fill" width={24} height={24} />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ overflowY: 'auto', px: 3, py: 2 }}>
        <FormProvider methods={methods}>
          {!previewMode ? (
              <Stack spacing={3} maxWidth={600} mx="auto">
                {/* Centered avatar and upload */}
                <Box textAlign="center" mb={3}>
                  <Typography variant="subtitle2" gutterBottom>
                    User Image
                  </Typography>
                  <Stack direction="row" spacing={3} justifyContent="center" alignItems="center" flexWrap="wrap">
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                        Current
                      </Typography>
                      <Avatar
                        src={user.image_url || ''}
                        alt="Current image"
                        sx={{ width: 96, height: 96, border: '1px solid #ccc', borderRadius: 2 }}
                      />
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                        New
                      </Typography>
                      {imagePreview ? (
                        <Avatar
                          src={imagePreview}
                          alt="New preview"
                          sx={{ width: 96, height: 96, border: '3px solid #d32f2f', borderRadius: 2 }}
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          No Change
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                  <input
                    accept="image/*"
                    type="file"
                    style={{ display: 'none' }}
                    id="upload-image"
                    onChange={(e) => setValue('imageFile', e.target.files, { shouldValidate: true })}
                  />
                  <label htmlFor="upload-image">
                    <Button variant="outlined" component="span" size="medium" sx={{ mt: 1 }}>
                      Upload New Image
                    </Button>
                  </label>
                </Box>

                {/* First and Last Name on the same line */}
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={6}>
                    <RHFTextField name="first_name" label="First Name" required fullWidth size="small" />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <RHFTextField name="last_name" label="Last Name" required fullWidth size="small" />
                  </Grid>
                </Grid>

                <RHFTextField name="display_name" label="Display Name" required fullWidth size="small" />
                <RHFTextField name="email" label="Email" required fullWidth size="small" />
                <RHFTextField name="phone" label="Phone" fullWidth size="small" />
                <RHFTextField name="referral_id" label="Referral / Promo Code" fullWidth size="small" />

                <RHFTextField
                  name="confirmPassword"
                  label="Confirm Admin Password"
                  required
                  fullWidth
                  size="small"
                  type="password"
                  autoComplete="current-password"
                  helperText="Enter your password to confirm changes"
                  error={Boolean(errors.confirmPassword)}
                />
                {errors.confirmPassword && (
                  <Typography variant="caption" color="error" sx={{ mt: -1, mb: 1 }}>
                    {errors.confirmPassword.message}
                  </Typography>
                )}

                {serverError && <Alert severity="error">{serverError}</Alert>}
              </Stack>
          ) : (
            <Box sx={{ maxWidth: 600, mx: 'auto' }}>
              <PreviewField label="First Name" field="first_name" />
              <PreviewField label="Last Name" field="last_name" />
              <PreviewField label="Display Name" field="display_name" />
              <PreviewField label="Email" field="email" />
              <PreviewField label="Phone" field="phone" />
              <PreviewField label="Referral ID" field="referral_id" />
              <PreviewField label="Image" field="image" isImage />
              {serverError && <Alert severity="error" sx={{ mt: 1 }}>{serverError}</Alert>}
            </Box>
          )}
        </FormProvider>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {!previewMode ? (
          <>
            <Button onClick={handleClose} color="inherit" variant="outlined">
              Cancel
            </Button>
            <Button onClick={handlePreview} variant="contained" color="primary" disabled={isSubmitting}>
              Preview Changes
            </Button>
          </>
        ) : (
          <>
            <Button onClick={() => setPreviewMode(false)} color="inherit" variant="outlined">
              Back to Edit
            </Button>
            <LoadingButton onClick={handleSubmit(onSubmit)} variant="contained" color="primary" loading={isSubmitting}>
              Confirm & Update
            </LoadingButton>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
